import { parseJson } from '../utils/safeJson'

export const ORG_ID_KEY = 'orgId'
export const ORG_DATA_KEY = 'orgData'
export const MULTI_ORG_IDS_KEY = 'multiOrgIds'
export const MULTI_ORG_SELECTED_KEYS_KEY = 'multiOrgSelectedKeys'
export const MULTI_ORG_DATA_KEY = 'multiOrgData'
export const WITH_CHILD_ORGS_KEY = 'withChildOrgs'

function readJson(storage, key, fallback) {
  return parseJson(storage.getItem(key), fallback)
}

function normalizeOrgId(value, fallback = null) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return fallback
  }

  if (typeof value === 'object') {
    return value.id ?? value.key ?? fallback
  }

  return value
}

export function normalizeMultiOrgIds(value) {
  const parsed = parseJson(value, value)

  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => {
        const id = normalizeOrgId(item)
        return id === null ? null : { id }
      })
      .filter(Boolean)
  }

  if (parsed && typeof parsed === 'object') {
    return Object.keys(parsed).map((id) => ({ id }))
  }

  return []
}

export function normalizeSelectedKeys(value) {
  const parsed = parseJson(value, value)

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {}
  }

  return Object.keys(parsed).reduce((keys, id) => {
    const selected = parsed[id]
    keys[id] = selected && typeof selected === 'object' ? selected : { checked: Boolean(selected) }
    return keys
  }, {})
}

export function selectedKeysToOrgIds(selectedKeys) {
  const normalizedSelectedKeys = normalizeSelectedKeys(selectedKeys)

  return Object.keys(normalizedSelectedKeys)
    .filter((id) => normalizedSelectedKeys[id]?.checked !== false)
    .map((id) => ({ id }))
}

export function orgIdsToSelectedKeys(orgIds) {
  return normalizeMultiOrgIds(orgIds).reduce((keys, item) => {
    keys[item.id] = { checked: true }
    return keys
  }, {})
}

export function readOrganizationState(storage = localStorage) {
  const multiOrgIds = normalizeMultiOrgIds(readJson(storage, MULTI_ORG_IDS_KEY, []))
  const multiOrgSelectedKeys = normalizeSelectedKeys(readJson(storage, MULTI_ORG_SELECTED_KEYS_KEY, {}))

  return {
    mode: multiOrgIds.length ? 'multi' : 'single',
    orgId: normalizeOrgId(storage.getItem(ORG_ID_KEY), null),
    orgData: readJson(storage, ORG_DATA_KEY, []),
    multiOrgIds,
    multiOrgSelectedKeys,
    multiOrgData: readJson(storage, MULTI_ORG_DATA_KEY, []),
    withChildOrgs: storage.getItem(WITH_CHILD_ORGS_KEY) === 'true',
  }
}

export function writeOrganizationState(state, storage = localStorage) {
  const nextState = {
    ...readOrganizationState(storage),
    ...state,
  }

  if (nextState.orgId !== null && typeof nextState.orgId !== 'undefined') {
    storage.setItem(ORG_ID_KEY, String(nextState.orgId))
  }

  storage.setItem(ORG_DATA_KEY, JSON.stringify(nextState.orgData ?? []))
  storage.setItem(MULTI_ORG_IDS_KEY, JSON.stringify(normalizeMultiOrgIds(nextState.multiOrgIds)))
  storage.setItem(MULTI_ORG_SELECTED_KEYS_KEY, JSON.stringify(normalizeSelectedKeys(nextState.multiOrgSelectedKeys)))
  storage.setItem(MULTI_ORG_DATA_KEY, JSON.stringify(nextState.multiOrgData ?? []))
  storage.setItem(WITH_CHILD_ORGS_KEY, nextState.withChildOrgs === true ? 'true' : 'false')

  return readOrganizationState(storage)
}

export function clearOrganizationState(storage = localStorage) {
  storage.removeItem(ORG_ID_KEY)
  storage.removeItem(ORG_DATA_KEY)
  storage.removeItem(MULTI_ORG_IDS_KEY)
  storage.removeItem(MULTI_ORG_SELECTED_KEYS_KEY)
  storage.removeItem(MULTI_ORG_DATA_KEY)
  storage.removeItem(WITH_CHILD_ORGS_KEY)
}
